import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import ExerciseIcon from '@pelckmans/elementis/icons/exercise';
import FileIcon from '@pelckmans/elementis/icons/file';
import { ContextType, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isExternalMedia, isFile, Medialink } from '../../../api/medialink';
import { getFileTypeDescription } from '../../../utils/fileType';
import { getFullHierarchyDescriptionOfNode } from '../../../utils/getNodeDescription';
import ModuleContext from '../_contexts/ModuleContext';
import MaterialPills from '../toc/components/MaterialPills';
import { MaterialIcon } from './MaterialIcon';
import ModuleHeaderTag from './ModuleHeaderTag';

const exerciseCategoryNames = (material: Medialink) => {
  return (material.exerciseCategories || [])
    .map(category => category.name)
    .sort()
    .join(', ');
};

type Props = {
  module: NonNullable<ContextType<typeof ModuleContext>['selectedModule']>;
  material: Medialink;
};

export function MedialinkDialogHeaderTagsWithoutPillGroup({ material, module }: Props) {
  const { t } = useTranslation();

  const { isAdaptive, isOnTheFly } = material?.assessmentQExercise || {};

  const fileType = useMemo((): string | undefined => {
    if (isExternalMedia(material)) {
      return t(`FILE_TYPE_DESCRIPTIONS.${getFileTypeDescription(material.externalMedia.type).key}`);
    }

    if (isFile(material)) {
      return t(`FILE_TYPE_DESCRIPTIONS.${getFileTypeDescription(material.file.s3file.mimeType).key}`);
    }

    return undefined;
  }, [material, t]);

  const exerciseCategory = useMemo(() => {
    const exerciseCategoryName = exerciseCategoryNames(material);

    if (
      module?.hasExerciseEntrance &&
      exerciseCategoryName &&
      ['exercise-edumatic', 'exercise-eduhint', 'exercise-assessmentq', 'mini-dialog', 'word-list'].includes(
        material.kind,
      )
    ) {
      return exerciseCategoryName;
    }

    return undefined;
  }, [material, module?.hasExerciseEntrance]);

  return (
    <>
      <ModuleHeaderTag module={module} />
      <Pill key="toc" icon={<ChapterIcon />}>
        {getFullHierarchyDescriptionOfNode(material.hierarchy, t)}
      </Pill>
      {exerciseCategory && (
        <Pill key="exercise-category" icon={<ExerciseIcon />}>
          {exerciseCategory}
        </Pill>
      )}
      <Pill
        key="content-type"
        icon={<MaterialIcon materialType="medialink" contentType={material.contentType} size="xs" />}
      >
        {material.contentType.name}
      </Pill>
      {fileType && (
        <Pill key="file-type" icon={<FileIcon />}>
          {fileType}
        </Pill>
      )}
      <MaterialPills isAdaptive={isAdaptive} isOnTheFly={isOnTheFly} key="material-pills" />
    </>
  );
}

export function MedialinkDialogHeaderTags({ material, module }: Props) {
  return (
    <PillGroup aria-label={undefined} role={undefined}>
      <MedialinkDialogHeaderTagsWithoutPillGroup material={material} module={module} />;
    </PillGroup>
  );
}
